/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('DOMContentLoaded', function () {
  class PkCollapsibleText extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.pk-collapsible-text__content');
      this.button = this.querySelector('.pk-collapsible-text__button');

      if (!this.content || !this.button) {
        console.error('PkCollapsibleText requires a content element and a button.');
        return;
      }

      requestAnimationFrame(() => {
        if (this.content.scrollHeight <= this.content.clientHeight) {
          this.content.classList.remove('pk-collapsible-text__content--collapsed');
          this.button.remove();
          return;
        }

        this.button.hidden = false;
        this.button.addEventListener('click', () => {
          this.content.classList.remove('pk-collapsible-text__content--collapsed');
          this.button.setAttribute('aria-expanded', 'true');
          this.button.remove();
        }, { once: true });
      });
    }
  }

  customElements.get('pk-collapsible-text') || customElements.define('pk-collapsible-text', PkCollapsibleText);

  /**
   * Custom element that provides expandable content functionality,
   * "show more/show less" use case, it' not a dropdown.
   *
   * Required data attributes:
   * @attribute data-content {HTMLElement} Required. The content element to be toggled.
   * @attribute data-toggle-button {HTMLElement} Required. The button element used for toggling the content.
   */
  class PkExpandableContent extends HTMLElement {
    constructor() {
      super();
  
      this.content = null;
      this.toggleButton = null;
      this.isExpanded = false;
  
      this.toggleContent = this.toggleContent.bind(this);
    }

    connectedCallback() {
      try {
        this.init();
      } catch (error) {
        console.error('PkExpandableContent initialization error:', error.message);
      }
    }

    disconnectedCallback() {
      this.toggleButton.removeEventListener('click', this.toggleContent);
    }
    
    init() {
      this.content = this.querySelector('[data-content]');
      this.toggleButton = this.querySelector('[data-button]');
  
      if (!this.content || !this.toggleButton) {
        throw new Error('Required data-content or data-button elements are missing in the DOM.');
      }
  
      if (!this.validateMaxHeightVariable()) {
        throw new Error('Missing or invalid --pk-expandable-content-max-height CSS variable. Set a non-negative value.');
      }
  
      if (!this.shouldDisplayToggleButton()) {
        this.toggleButton.remove();
        return;
      }
  
      this.toggleButton.classList.add('pk-expandable-content__toggle-button--visible');
      
      this.initButtonLabels();
      this.setInitialState();
      this.attachEventListeners();
    }
  
    initButtonLabels() {
      const labels = this.toggleButton.getAttribute('data-button');
      if (!labels) {
        throw new Error('data-button attribute is missing or empty.');
      }
  
      const [showMoreLabel, showLessLabel] = labels.split(',');
      if (!showMoreLabel || !showLessLabel) {
        throw new Error('data-button attribute must contain two labels separated by a comma, e.g., "Show more,Show less".');
      }
  
      this.showMoreLabel = showMoreLabel.trim();
      this.showLessLabel = showLessLabel.trim();
    }
  
    setInitialState() {
      const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
  
      this.content.style.maxHeight = maxHeight;
      this.content.setAttribute('aria-expanded', 'false');
      this.toggleButton.textContent = this.showMoreLabel;
    }
  
    attachEventListeners() {
      this.toggleButton.addEventListener('click', this.toggleContent);
    }
  
    toggleContent() {
      this.isExpanded = !this.isExpanded;
      const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
  
      this.content.style.maxHeight = this.isExpanded ? 'none' : maxHeight;
      this.content.setAttribute('aria-expanded', this.isExpanded);
      this.toggleButton.textContent = this.isExpanded
        ? this.showLessLabel
        : this.showMoreLabel;
    }
  
    /**
     * Validate the `--pk-expandable-content-max-height` CSS variable to ensure it is valid.
     * @returns {boolean} True if the variable is valid, false otherwise.
     */
    validateMaxHeightVariable() {
      const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
      const numericHeight = parseInt(maxHeight, 10);
      
      return !Number.isNaN(numericHeight) && numericHeight >= 0;
    }
  
    /**
     * Determines whether the toggle button should be displayed based on the content's height.
     * @returns {boolean} True if the button should be displayed, false otherwise.
     */
    shouldDisplayToggleButton() {
      const maxHeight = parseInt(this.getCSSVariable('--pk-expandable-content-max-height'), 10);
      const contentHeight = this.content.scrollHeight;
      
      return contentHeight > maxHeight;
    }
  
    /**
     * @param {string} variableName - The name of the CSS variable to retrieve.
     * @returns {string|null} The value of the CSS variable, or null if not set.
     */
    getCSSVariable(variableName) {
      return getComputedStyle(this.content).getPropertyValue(variableName).trim();
    }
  }
  
  customElements.get('pk-expandable-content') || customElements.define('pk-expandable-content', PkExpandableContent);
});
